import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import type { UserAvailability } from "@bezoned/shared/types/user-presence.js";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Toaster,
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import { BezonedMenuTabs } from "../../../components/ui/common/bezoned-menu-tabs.js";
import type { BezonedMenuItem } from "../../../components/ui/common/bezoned-menu-tabs.js";
import { useToast } from "../../../hooks/use-toast.js";
import { teamOfficeTableUpdate } from "../../../services/api.service.js";
import { recordErrorInSentry } from "../../../utils/init-sentry.js";
import { getTable } from "../../fluid/fluid.service.js";
import { useFluid } from "../../fluid/hooks/use-fluid.js";
import { useOffice } from "../../office/use-office.js";
import { TableSettingsGeneralTab } from "./table-settings-general-tab.js";

const TAB_LABELS = ["General"] as const;

type TabLabel = (typeof TAB_LABELS)[number];

interface TableSettingsDialogProps {
  onClose?: () => void;
  fluidTableId: string;
  presetTableId: string;
  tableName: string | null;
}

export const TableSettingsDialog = ({
  onClose,
  fluidTableId,
  presetTableId,
  tableName,
}: TableSettingsDialogProps) => {
  const { sharedTree } = useFluid();
  const { isTeamAdmin, team, tables } = useOffice();
  const queryClient = useQueryClient();
  const { toasterId, dispatchSuccessToast, dispatchFailureToast } = useToast();
  const [selectedTab, setSelectedTab] = useState<TabLabel>("General");

  const table = getTable({ sharedTree, tableId: fluidTableId });
  const currentDefaultStatus =
    (table?.defaultStatus as UserAvailability) ?? null;
  const currentName = tableName ?? "";

  const existingTableNames = useMemo(
    () =>
      tables
        .filter((t) => t.presetTableId !== presetTableId)
        .map((t) => t.name)
        .filter((name): name is string => name !== null),
    [tables, presetTableId]
  );

  const handleSave = useCallback(
    async ({
      name,
      defaultStatus,
    }: {
      name: string;
      defaultStatus: UserAvailability | undefined;
    }) => {
      if (!team) {
        dispatchFailureToast({
          message: "No office selected. Please try again.",
        });
        return;
      }

      try {
        const nameChanged = name !== currentName;
        const statusChanged = (defaultStatus ?? null) !== currentDefaultStatus;

        if (nameChanged) {
          await teamOfficeTableUpdate({
            msTeamId: team.msTeamId,
            name,
            presetTableId,
          });
        }

        if (statusChanged && table) {
          table.setDefaultStatus(defaultStatus);
        }

        if (nameChanged) {
          queryClient.invalidateQueries({
            queryKey: ["teamOfficeGet", team.msTeamId],
          });
        }

        dispatchSuccessToast({ message: "Table settings saved." });
        onClose?.();
      } catch (error) {
        recordErrorInSentry(
          error instanceof Error ? error : new Error(String(error))
        );
        dispatchFailureToast({
          message: "Failed to save table settings. Please try again.",
        });
        throw error;
      }
    },
    [
      currentDefaultStatus,
      currentName,
      dispatchFailureToast,
      dispatchSuccessToast,
      onClose,
      table,
      presetTableId,
      team,
      queryClient.invalidateQueries,
    ]
  );

  const menuItems = useMemo<BezonedMenuItem[]>(
    () => [
      {
        component: (
          <TableSettingsGeneralTab
            existingTableNames={existingTableNames}
            initialDefaultStatus={currentDefaultStatus}
            initialName={currentName}
            isTeamAdmin={isTeamAdmin}
            key={`${fluidTableId}-general`}
            onSave={handleSave}
          />
        ),
        label: "General",
      },
    ],
    [
      currentDefaultStatus,
      currentName,
      existingTableNames,
      fluidTableId,
      handleSave,
      isTeamAdmin,
    ]
  );
  const handleSelectTab178 = useStableCallback((tab) => {
    if (TAB_LABELS.includes(tab as TabLabel)) {
      setSelectedTab(tab as TabLabel);
    }
  });

  const handleOpenChange154 = useStableCallback((_event, data) => {
    if (!data.open) {
      onClose?.();
    }
  });

  return (
    <>
      <Dialog onOpenChange={handleOpenChange154} open={true}>
        <DialogSurface className="relative sm:max-h-[90lvh] sm:w-[90lvw] md:min-w-[600px]">
          <Button
            appearance="subtle"
            aria-label="Close dialog"
            className="absolute top-4 right-4 z-10"
            icon={<Dismiss24Regular />}
            onClick={onClose}
            type="button"
          />
          <DialogBody className="flex flex-col sm:max-h-[90lvh]">
            <DialogTitle>Table settings</DialogTitle>

            <DialogContent className="flex flex-1 flex-col overflow-hidden lg:flex-row">
              <div className="border-bzd-border-100 w-full border-b p-4 lg:h-full lg:w-[200px] lg:flex-shrink-0 lg:border-r lg:border-b-0">
                <BezonedMenuTabs
                  currentTab={selectedTab}
                  items={menuItems}
                  selectTab={handleSelectTab178}
                />
              </div>
              <div className="flex flex-1 flex-col overflow-y-auto px-6 md:min-h-[300px]">
                {menuItems.find((item) => item.label === selectedTab)
                  ?.component ?? <div>This tab is not available</div>}
              </div>
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <Toaster toasterId={toasterId} />
    </>
  );
};
